import { FeedItemDto } from '@act/shared/data-transfer-objects';
import DayBreaker from './day-breaker';
import FeedCallCard from './feed-card/feed-call-card';
import CompletedWorkflowPhaseFeedCard from './feed-card/feed-card-completed-workflow-phase';
import MemberTransferRequestFeedCard from './feed-card/feed-member-transfer-request-card';
import FeedMessageCard from './feed-card/feed-message-card';
import FeedNoteCard from './feed-card/feed-note-card';
import MemberStatusChangeRequestFeedCard from './feed-card/feed-status-change-request-card';
import FeedVisitCard from './feed-card/feed-visit-card';
import SurveyCard from './feed-card/survey-card';
import { forwardRef, memo } from 'react';
import HospitalizationFeedCard from './feed-card/feed-hospitalization-card';

interface FeedItemEventProps {
  feedItem: FeedItemDto;
  previousTimestamp: number;
  isLastEventItem: boolean;
  shouldHighlight?: boolean;
  additionalDialogs?: { [key: string]: (...args: unknown[]) => void };
}

const SingleFeedItemComponent = forwardRef<
  HTMLDivElement,
  Omit<FeedItemEventProps, 'ref'>
>(
  (
    {
      feedItem,
      previousTimestamp,
      isLastEventItem,
      shouldHighlight,
      additionalDialogs,
    },
    ref
  ) => {
    return (
      <>
        <DayBreaker
          time={feedItem.timestamp}
          previousTime={previousTimestamp}
        ></DayBreaker>
        <div ref={ref}>
          {(() => {
            switch (feedItem.externalItemType) {
              case 'message':
                return (
                  <FeedMessageCard
                    messageId={feedItem.externalItemId}
                    isLastEventItem={isLastEventItem}
                    shouldHighlight={shouldHighlight}
                  ></FeedMessageCard>
                );
              case 'call':
                return (
                  <FeedCallCard
                    callId={feedItem.externalItemId}
                    isLastEventItem={isLastEventItem}
                    shouldHighlight={shouldHighlight}
                  ></FeedCallCard>
                );
              case 'note':
                return (
                  <FeedNoteCard
                    noteId={feedItem.externalItemId}
                    shouldHighlight={shouldHighlight}
                  ></FeedNoteCard>
                );
              case 'visit':
                return (
                  <FeedVisitCard
                    visitId={feedItem.externalItemId}
                    shouldHighlight={shouldHighlight}
                  ></FeedVisitCard>
                );
              case 'member_status_change_request':
                return (
                  <MemberStatusChangeRequestFeedCard
                    feedItem={feedItem}
                    shouldHighlight={shouldHighlight}
                    memberStatusChangeRequestId={feedItem.externalItemId}
                  ></MemberStatusChangeRequestFeedCard>
                );
              case 'member_transfer_request':
                return (
                  <MemberTransferRequestFeedCard
                    shouldHighlight={shouldHighlight}
                    feedItem={feedItem}
                    memberTransferRequestId={feedItem.externalItemId}
                    additionalDialogs={additionalDialogs}
                  />
                );
              case 'assigned_workflow_phase':
                return (
                  <CompletedWorkflowPhaseFeedCard
                    shouldHighlight={shouldHighlight}
                    feedItem={feedItem}
                  />
                );
              case 'assigned_survey':
                return (
                  <SurveyCard
                    assignedSurveyId={feedItem.externalItemId}
                    completedAt={feedItem.createdAt}
                    shouldHighlight={shouldHighlight}
                  />
                );
              case 'hospitalization':
                return (
                  <HospitalizationFeedCard
                    feedItem={feedItem}
                    shouldHighlight={shouldHighlight}
                  />
                );
              default:
                return <div>...</div>;
            }
          })()}
        </div>
      </>
    );
  }
);

SingleFeedItemComponent.displayName = 'SingleFeedItem';

// Memoize to prevent re-renders when props haven't changed
const SingleFeedItem = memo(SingleFeedItemComponent);

export default SingleFeedItem;
